import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/nucleo/utilitarios";
import { AlertCircle, Info, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Slot ID rendered by `<PageHeader>`. Banners portal into this node
 * so the page header is ALWAYS rendered above any banner/aviso.
 *
 * Regra global de UI (acordada em 2026-05-13): banners de aviso, info,
 * homologação ou alerta devem aparecer SEMPRE abaixo do PageHeader,
 * nunca acima do título da tela.
 */
export const PAGE_BANNERS_SLOT_ID = "page-banners-slot";

type Tone = "info" | "warning" | "destructive" | "success";

const TONE_STYLES: Record<Tone, string> = {
  info: "bg-primary/5 border-primary/30 text-foreground",
  warning: "bg-warning/15 border-warning/50 text-foreground",
  destructive: "bg-destructive/10 border-destructive/50 text-foreground",
  success: "bg-success/15 border-success/50 text-foreground",
};

const TONE_ICON_COLOR: Record<Tone, string> = {
  info: "text-primary",
  warning: "text-warning",
  destructive: "text-destructive",
  success: "text-success",
};

const TONE_TITLE_COLOR: Record<Tone, string> = {
  info: "text-foreground",
  warning: "text-foreground",
  // Título é texto: `destructive-text`, o par AA nos dois temas (ver contraste.ts).
  destructive: "text-destructive-text",
  success: "text-foreground",
};

const TONE_ICONS: Record<Tone, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  destructive: AlertCircle,
  success: CheckCircle2,
};

interface PageBannerProps {
  title?: ReactNode;
  description?: ReactNode;
  tone?: Tone;
  icon?: typeof Info;
  children?: ReactNode;
  className?: string;
}

export function PageBanner({
  title,
  description,
  tone = "info",
  icon,
  children,
  className,
}: PageBannerProps) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById(PAGE_BANNERS_SLOT_ID);
    setSlot(el);
  }, []);

  const Icon = icon ?? TONE_ICONS[tone];

  const node = (
    <div
      role="status"
      className={cn(
        "rounded-md border px-4 py-3 flex items-start gap-3",
        TONE_STYLES[tone],
        className
      )}
    >
      <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", TONE_ICON_COLOR[tone])} />
      <div className="min-w-0 space-y-1">
        {title && <h4 className={cn("font-semibold text-sm leading-tight", TONE_TITLE_COLOR[tone])}>{title}</h4>}
        {description && (
          <div className="text-sm leading-snug text-foreground/90">
            {typeof description === "string" ? <p>{description}</p> : description}
          </div>
        )}
        {children}
      </div>
    </div>
  );

  // Fallback: se não houver slot, renderiza inline (mantém compatibilidade).
  if (!slot) return node;
  return createPortal(node, slot);
}
